import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import WorkshopCard from './WorkshopCard';
import { useTranslation } from 'react-i18next';
import './Workshops.css';

const Workshops = () => {
  const { t, i18n } = useTranslation();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef(null);
  
  // Language detection
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Load fonts and add meta tags
  useEffect(() => {
    // Load Cormorant Garamond font
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700"]')) {
      const garamondLink = document.createElement('link');
      garamondLink.rel = 'stylesheet';
      garamondLink.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(garamondLink);
    }
    
    // Set page title and meta description for SEO
    document.title = t('workshop_section.page_title') || t('workshop_section.discover_our_workshops');
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', t('workshop_section.meta_description') || t('workshop_section.description'));
  }, [t]);

  // Add SEO metadata and structured data for workshops section
  useEffect(() => {
    if (isSlovak && workshops.length > 0) {
      // Create schema.org data for workshops collection
      const workshopsData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": workshops.map((workshop, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Event",
            "name": workshop.title,
            "startDate": workshop.start_time,
            "location": {
              "@type": "Place",
              "name": workshop.location || "TiDeaSolis Yoga štúdio",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Rožňava",
                "addressCountry": "SK"
              }
            },
            "performer": {
              "@type": "Person",
              "name": workshop.instructor
            },
            "offers": {
              "@type": "Offer",
              "price": workshop.price || 0,
              "priceCurrency": "EUR",
              "availability": workshop.available_spots > 0 ? 
                "https://schema.org/InStock" : 
                "https://schema.org/SoldOut"
            }
          }
        }))
      };
      
      // Add or update JSON-LD script
      let scriptTag = document.getElementById('workshops-schema');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'workshops-schema';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(workshopsData);
    }
    
    return () => {
      // Optional cleanup
      const script = document.getElementById('workshops-schema');
      if (script) {
        script.remove();
      }
    };
  }, [workshops, isSlovak]);

  // Handle carousel scroll events to update pagination
  useEffect(() => {
    const handleScroll = () => {
      if (!carouselRef.current || !isMobile) return;
      
      const scrollPosition = carouselRef.current.scrollLeft;
      const itemWidth = carouselRef.current.offsetWidth;
      const newActiveSlide = Math.round(scrollPosition / itemWidth);
      
      if (newActiveSlide !== activeSlide) {
        setActiveSlide(newActiveSlide);
      }
    };
    
    const carousel = carouselRef.current;
    if (carousel && isMobile) {
      carousel.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (carousel && isMobile) {
        carousel.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isMobile, activeSlide]);

  // Scroll to a specific slide
  const scrollToSlide = (index) => {
    if (!carouselRef.current) return;
    
    const itemWidth = carouselRef.current.offsetWidth;
    carouselRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth'
    });
    setActiveSlide(index);
  };

  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        setLoading(true);
        // console.log('Fetching workshops from dedicated workshops endpoint');
        const response = await axios.get('workshops');
        
        // console.log('Workshops API response:', response.data);
        
        // Calculate the date 2 months from now
        const now = new Date();
        // console.log('Current date for filtering:', now);
        
        const twoMonthsFromNow = new Date();
        twoMonthsFromNow.setMonth(now.getMonth() + 2);
        // console.log('Two months from now date for filtering:', twoMonthsFromNow);
        
        // Get workshops array from the response
        const workshopsData = response.data.workshops || [];
        // console.log('Workshops data to filter:', workshopsData);
        
        // Filter workshops to only include those within the next 2 months
        const filteredWorkshops = workshopsData.filter(workshop => {
          // Try to get date from start_time or date_time field
          const dateStr = workshop.start_time || workshop.date_time;
          // console.log(`Workshop "${workshop.title || workshop.name || workshop.class_name}" date string:`, dateStr);
          
          const workshopDate = new Date(dateStr);
          
          // Add other fields the workshop card might need if they're missing
          if (!workshop.title && workshop.class_name) {
            workshop.title = workshop.class_name;
          }
          
          if (!workshop.start_time && workshop.date_time) {
            workshop.start_time = workshop.date_time;
          }
          
          if (!workshop.instructor && workshop.instructor_name) {
            workshop.instructor = workshop.instructor_name;
          }
          
          // console.log(`Workshop "${workshop.title || workshop.name || workshop.class_name}" date: ${workshopDate}, included: ${workshopDate >= now && workshopDate <= twoMonthsFromNow}`);
          return workshopDate >= now && workshopDate <= twoMonthsFromNow;
        });
        
        // console.log(`Filtered from ${workshopsData.length} to ${filteredWorkshops.length} workshops`);
        
        setWorkshops(filteredWorkshops);
        setLoading(false);
      } catch (err) {
        // console.error("Error fetching workshops:", err);
        setError(t('workshop_section.failed_load_workshops'));
        setLoading(false);
      }
    };

    fetchWorkshops();
  }, [t]);

  // Loading state - simplified layout
  if (loading) {
    return (
      <section className="workshops-section" lang={currentLanguage}>
        <div className="container">
          <h2>{t('workshop_section.upcoming_workshops')}</h2>
          <div className="loading-spinner">{t('loader.loading')}</div>
        </div>
      </section>
    );
  }

  // Error state - simplified layout
  if (error) {
    return (
      <section className="workshops-section" lang={currentLanguage}>
        <div className="container">
          <h2>{t('workshop_section.upcoming_workshops')}</h2>
          <div className="error-message" role="alert">{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="workshops-section" 
      id="workshops" 
      lang={currentLanguage}
      itemScope 
      itemType="https://schema.org/CollectionPage"
    >
      <meta itemProp="name" content={isSlovak ? "Workshopy Jogy" : "Yoga Workshops"} />
      <meta itemProp="description" content={isSlovak ? 
        "Workshopy jogy a špeciálne podujatia v našom štúdiu v nasledujúcich dvoch mesiacoch." : 
        "Yoga workshops and special events at our studio in the next two months."} 
      />
      
      <div className="workshops-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="section-header">
            <h2 itemProp="headline">{t('workshop_section.discover_our_workshops')}</h2>
            <p className="section-description" itemProp="about" lang={currentLanguage}>
              {t('workshop_section.immerse_yourself')}
            </p>
          </div>
        </div>
      </div>
      
      <div className="container">
        <div className="section-header">
          <h2 lang={currentLanguage}>{t('workshop_section.upcoming_workshops')}</h2>
          <p className="section-description" lang={currentLanguage}>
            {t('workshop_section.browse_upcoming')}
          </p>
        </div>
        
        {workshops.length === 0 ? (
          // No workshops available
          <p className="no-workshops-message">
            {t('workshop_section.no_workshops_message')}
          </p>
        ) : isMobile ? (
          // Mobile Layout - Swipeable Carousel with Pagination
          <div className="mobile-workshops-container" role="region" aria-label={isSlovak ? "Workshopy na mobilnom zariadení" : "Workshops on mobile device"}>
            <div 
              className="workshops-carousel" 
              ref={carouselRef}
              role="list"
            >
              {workshops.map((workshop, index) => (
                <div key={workshop.id} className="carousel-item" role="listitem">
                  <WorkshopCard workshop={workshop} />
                </div>
              ))}
            </div>
            
            {/* Pagination Indicators */}
            <div 
              className="carousel-pagination" 
              role="tablist"
              aria-label={isSlovak ? "Navigácia workshopov" : "Workshop navigation"}
            >
              {workshops.map((_, index) => (
                <div 
                  key={index}
                  className={`pagination-dot ${index === activeSlide ? 'active' : ''}`}
                  onClick={() => scrollToSlide(index)}
                  role="tab"
                  aria-selected={index === activeSlide}
                  aria-label={isSlovak ? 
                    `Workshop ${index + 1} z ${workshops.length}` : 
                    `Workshop ${index + 1} of ${workshops.length}`
                  }
                  tabIndex={0}
                />
              ))}
            </div>
          </div>
        ) : (
          // Desktop Layout - Original Grid
          <div className="workshops-grid" role="list" itemScope itemType="https://schema.org/ItemList">
            {workshops.map((workshop, index) => (
              <div key={workshop.id} role="listitem" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <meta itemProp="position" content={index + 1} />
                <div itemProp="item" itemScope itemType="https://schema.org/Event">
                  <WorkshopCard workshop={workshop} />
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="decorative-leaf left-leaf" aria-hidden="true"></div>
        <div className="decorative-leaf right-leaf" aria-hidden="true"></div>
      </div>
    </section>
  );
};

export default Workshops; 